import { loc } from '../data/cards'
import { CATALOG_SECTIONS, FORMATS, formatDesc, formatName, type FormatId } from '../data/formats'
import { SPREADS, spreadDesc, spreadName, type SpreadId } from '../data/spreads'
import { useSirr } from '../store'

interface Props {
  onStartSpread: (id: SpreadId) => void
  onOpenFormat: (id: FormatId) => void
}

/**
 * Каталог «Гадания»: расклады Таро плюс гадания без карт, разложенные по
 * секциям — по фото, словом, персонажи.
 *
 * Секции, а не пятая вкладка: фотографии, сны и персонажи не сводятся
 * к одному имени вкладки, а подписи на азербайджанском и турецком длиннее
 * русских настолько, что пятая уже не влезает в ряд (см. `TabBar`).
 */
export function Catalog({ onStartSpread, onOpenFormat }: Props) {
  const { profile, lang, t, remaining, included, freeFortune } = useSirr()

  return (
    <div className="screen">
      <p className="prose" style={{ color: 'var(--ink-muted)', marginBottom: 18 }}>
        {t('catalogIntro')}
      </p>

      {CATALOG_SECTIONS.map((section) => {
        const spreads = section.id === 'tarot' ? SPREADS : []
        const formats = section.formats
          .map((id) => FORMATS.find((f) => f.id === id))
          .filter((f) => f !== undefined)
        const count = spreads.length + formats.length
        if (!count) return null

        return (
          <section key={section.id}>
            <div className="catsection">
              <span className="catsection__title">{loc(section.title, lang)}</span>
              <span className="catsection__rule" />
              <span className="catsection__count">{count}</span>
            </div>

            <div className="grid-2" style={{ marginBottom: 24 }}>
              {spreads.map((sp) => {
                const free = sp.cost === 0
                // Подписка покрывает платные расклады, пока не выбран дневной
                // потолок. Показываем это ценой на плитке, а не мелким текстом
                // где-то ещё: подписчик должен видеть, за что заплатил, в тот
                // момент, когда выбирает расклад.
                const covered = !free && included > 0
                // Платный расклад не по карману — помечаем, но не блокируем:
                // тап уводит на экран пополнения, а не молча ничего не делает.
                const affordable = free || covered || profile.stars >= sp.cost
                return (
                  <Tile
                    key={sp.id}
                    sym={sp.sym}
                    name={spreadName(sp, lang)}
                    desc={spreadDesc(sp, lang)}
                    meta={`${sp.n} ${t('cards')}`}
                    note={sp.available && !affordable ? t('notEnough') : ''}
                    tag={
                      !sp.available ? t('soon') : free ? t('free') : covered ? t('inSub') : `✦ ${sp.cost}`
                    }
                    tone={
                      !sp.available ? 'muted' : free || covered ? 'free' : affordable ? 'paid' : 'short'
                    }
                    dimmed={!sp.available}
                    onClick={() => onStartSpread(sp.id)}
                  />
                )
              })}

              {/*
                «Первое бесплатно» на плитке стоит выше «не хватает ✦»:
                замерено, что до конца гадания доходили девять человек из ста
                пяти открывших, то есть «не хватает» и было тем, что видело
                большинство. Подписчику обещать нечего — у него включено.
              */}
              {formats.map((f) => {
                const covered = included > 0
                const affordable = covered || profile.stars >= f.cost
                return (
                  <Tile
                    key={f.id}
                    sym={f.sym}
                    name={formatName(f, lang)}
                    desc={formatDesc(f, lang)}
                    meta={t('newBadge')}
                    note={
                      !f.available || covered
                        ? ''
                        : freeFortune
                          ? t('firstFree')
                          : !affordable
                            ? t('notEnough')
                            : ''
                    }
                    tag={
                      !f.available
                        ? t('soon')
                        : covered
                          ? t('inSub')
                          : freeFortune
                            ? t('planFree')
                            : `✦ ${f.cost}`
                    }
                    tone={
                      !f.available ? 'muted' : covered || freeFortune ? 'free' : affordable ? 'paid' : 'short'
                    }
                    dimmed={!f.available}
                    onClick={() => onOpenFormat(f.id)}
                  />
                )
              })}
            </div>
          </section>
        )
      })}

      <p style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 2, lineHeight: 1.6 }}>
        {t('limitLeft', { n: remaining })}
        <br />
        {t('balance', { n: profile.stars })}
      </p>
    </div>
  )
}

/** Цвет плашки с ценой: бесплатно, по карману, не хватает, ещё не открыто. */
type Tone = 'free' | 'paid' | 'short' | 'muted'

const TAG_BORDER: Record<Tone, string> = {
  free: 'rgba(120,200,120,.4)',
  paid: 'rgba(200,162,78,.4)',
  short: 'rgba(224,122,84,.45)',
  muted: 'rgba(200,162,78,.25)',
}

const TAG_COLOR: Record<Tone, string> = {
  free: '#9fd39f',
  paid: '#e6c874',
  short: '#e07a54',
  muted: 'var(--ink-faint)',
}

function Tile({
  sym, name, desc, meta, note, tag, tone, dimmed, onClick,
}: {
  sym: string
  name: string
  desc: string
  meta: string
  note: string
  tag: string
  tone: Tone
  dimmed: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className="pick"
      style={{
        borderRadius: 16,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 9,
        opacity: dimmed ? 0.72 : 1,
      }}
      onClick={onClick}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            border: '1px solid rgba(200,162,78,.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            color: 'var(--gold)',
          }}
        >
          {sym}
        </div>
        <div
          style={{
            fontSize: 10.5,
            padding: '3px 9px',
            borderRadius: 20,
            border: `1px solid ${TAG_BORDER[tone]}`,
            color: TAG_COLOR[tone],
            whiteSpace: 'nowrap',
          }}
        >
          {tag}
        </div>
      </div>

      <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19, lineHeight: 1.1 }}>
        {name}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.4 }}>{desc}</div>
      <div className="eyebrow" style={{ fontSize: 9, letterSpacing: 1, color: 'rgba(200,162,78,.7)' }}>
        {meta}
        {note && <span style={{ color: '#e07a54', marginLeft: 6 }}>· {note}</span>}
      </div>
    </button>
  )
}
